// src/pages/healthcare/consultation/consultationComponents/Transcript.tsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { CSSTransition, TransitionGroup } from "react-transition-group";
import { Card, Button, Space, Typography, Empty, Avatar, Tooltip, Alert, Tag } from "antd";
import { AudioOutlined, AudioMutedOutlined, BulbOutlined, ArrowDownOutlined } from "@ant-design/icons";

// Types
import { User } from "../../../../types/admin/UserTypes";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import { ParticipantDisplayNames, SpeakerMap, SpeakerParticipantInput, Transcript } from "../../../../types/appointments/ConsultTypes";
import { TranscriptStatus } from "../../../../types/appointments/AppointmentTypes";
import { TranscriberStatus } from "../../../../hooks/consult/useConsultTranscripts";

// Utils
import { effectiveSpeakerLabel, resolveSpeakerName, speakerAssignmentFor } from "../../../../utils/consult/speakerNames";

// Audio Service
import AudioWave from "./AudioWave";
import { AudioStreamingService } from "../../../../services/audioStreamingService";
import SpeakerAssignPopover from "./SpeakerAssignPopover";

const { Text } = Typography;

/** How far off the bottom the practitioner has to scroll before auto-scroll stands down. */
const AUTO_SCROLL_THRESHOLD_PX = 80;

/**
 * Which transcript the practitioner is reading (S30 of 86d3zmpwv).
 *
 * ⚠️ An ALLOW-LIST, not a `!status` guard. A value the backend adds later must render as NOTHING
 * rather than as an unlabelled tag or a raw string on a clinician's screen — the same rule the
 * transcriber-health messages follow in `useConsultTranscripts`. A row written before S2 carries
 * no status at all and looks exactly as it did then.
 */
const TRANSCRIPT_STATUS_TAG: Record<TranscriptStatus, { color: string; label: string; tip: string }> = {
	live: { color: "warning", label: "Live transcript", tip: "Transcribed in the room as the consult happened. A more accurate version is still being prepared." },
	final: { color: "success", label: "Final transcript", tip: "The completed re-transcription of the consult recording." },
};

export const TranscriptStatusTag: React.FC<{ status?: TranscriptStatus }> = ({ status }) => {
	if (status !== "live" && status !== "final") return null;

	const { color, label, tip } = TRANSCRIPT_STATUS_TAG[status];

	return (
		<Tooltip title={tip}>
			<Tag color={color} data-testid="consultation-transcript-status" style={{ marginInlineEnd: 0, fontSize: 11 }}>
				{label}
			</Tag>
		</Tooltip>
	);
};

export interface TranscriptListProps {
	/** Named on the speaker chip's practitioner option. */
	practitioner: User;
	/** Named on the speaker chip's patient option. */
	patient: Patient;
	/** Oldest first — the feed is ordered by `useConsultTranscripts`, never here. */
	transcripts: Transcript[];
	transcriberActive: boolean;
	toggleTranscriber: () => void;
	isDisabled?: boolean;
	audioStreamingServiceRef: React.RefObject<AudioStreamingService>;
	/** participantId → display name, from the room's roster. */
	displayNames?: ParticipantDisplayNames;
	/** Speaker label → who that is, from `roomInfo` and `speakerMapUpdated`. */
	speakerMap?: SpeakerMap;
	/**
	 * Assign (or, with `null`, clear) a name for one speaker label. Its PRESENCE is the in-person
	 * gate: the video page passes no handler and its chips stay inert, because an online room's
	 * roster already names every microphone.
	 */
	onAssignSpeaker?: (speakerLabel: string, participant: SpeakerParticipantInput | null) => void;
	/** Transcriber health, or `null` when there is nothing to report. */
	transcriberStatus?: TranscriberStatus | null;
	/**
	 * Which pass produced these segments, from the stored record. Rendered here rather than in a
	 * drawer title so that every place either page shows a transcript carries the same badge —
	 * the video page renders this list twice.
	 */
	transcriptStatus?: TranscriptStatus;
}

const TranscriptList: React.FC<TranscriptListProps> = ({ practitioner, patient, transcripts, transcriberActive, toggleTranscriber, isDisabled, audioStreamingServiceRef, displayNames, speakerMap, onAssignSpeaker, transcriberStatus, transcriptStatus }) => {
	// ─── Auto-scroll ───────────────────────────────────────────────────────────────────────
	// The list is bottom-anchored and follows the newest segment, EXCEPT while the practitioner
	// is reading further up: yanking the viewport away mid-sentence is worse than a stale view,
	// and a consult produces a new segment every few seconds. Scrolling back to within
	// AUTO_SCROLL_THRESHOLD_PX of the bottom re-arms it, so "follow" needs no explicit opt-in.
	const scrollRef = useRef<HTMLDivElement>(null);
	const [pinnedToLatest, setPinnedToLatest] = useState<boolean>(true);

	const handleScroll = useCallback(() => {
		const el = scrollRef.current;
		if (!el) return;
		setPinnedToLatest(el.scrollHeight - el.scrollTop - el.clientHeight <= AUTO_SCROLL_THRESHOLD_PX);
	}, []);

	const jumpToLatest = useCallback(() => {
		const el = scrollRef.current;
		if (el) el.scrollTop = el.scrollHeight;
		setPinnedToLatest(true);
	}, []);

	// Keyed on the array identity, not its length: a revision replaces a segment in place and can
	// grow the content without adding a row.
	useEffect(() => {
		if (!pinnedToLatest) return;
		const el = scrollRef.current;
		if (el) el.scrollTop = el.scrollHeight;
	}, [transcripts, pinnedToLatest]);

	// The names the speaker chip offers. Both fall back rather than render " " — the video page's
	// patient and practitioner are null until their fetches land.
	const practitionerName = `${practitioner?.first_name || ""} ${practitioner?.last_name || ""}`.trim() || "Practitioner";
	const patientName = `${patient?.first_name || ""} ${patient?.last_name || ""}`.trim() || "Patient";

	// speakerTag's writer moved from PauseModal into InPersonConsultation's own startConsultation and still hardcodes ""; resolveSpeakerName is what actually names a speaker.
	const renderTranscriptContent = (t: Transcript) => {
		const speakerLabel = effectiveSpeakerLabel(t);
		const speakerName = resolveSpeakerName(t, speakerMap, displayNames);
		const assignment = speakerAssignmentFor(speakerLabel, speakerMap);
		// A completed consult is a record, not a room: there is no socket to carry an assignment.
		const canAssign = Boolean(onAssignSpeaker) && !isDisabled && Boolean(speakerLabel);

		const speakerChip = canAssign ? (
			<SpeakerAssignPopover speakerLabel={speakerLabel} assignment={assignment} practitionerName={practitionerName} patientName={patientName} onAssign={onAssignSpeaker!}>
				<Button type="text" size="small" data-testid="consultation-speaker-chip" style={{ height: 18, padding: "0 4px", fontSize: 11 }}>
					{speakerName}
				</Button>
			</SpeakerAssignPopover>
		) : (
			<Text type="secondary" style={{ fontSize: 11 }}>
				{speakerName}
			</Text>
		);

		return (
			<Space align="start">
				<Avatar size={32} style={{ backgroundColor: "#1890ff", flexShrink: 0 }}>
					{speakerName.charAt(0)}
				</Avatar>
				<div style={{ flex: 1 }}>
					<div style={{ fontSize: 13 }}>{t.transcript}</div>
					<Space size={8}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							{new Date(t.timestamp).toLocaleTimeString()}
						</Text>
						{speakerChip}
					</Space>
				</div>
			</Space>
		);
	};

	return (
		<div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
			<Space
				style={{
					marginBottom: 12,
					padding: "8px 12px",
					background: "var(--background-color)",
					borderRadius: 8,
					width: "100%",
					justifyContent: "space-between",
				}}
			>
				<Space>
					<Tooltip title={transcriberActive ? "Deactivate" : "Activate"}>
						<Button type={transcriberActive ? "primary" : "default"} danger={!transcriberActive} icon={transcriberActive ? <AudioOutlined /> : <AudioMutedOutlined />} onClick={toggleTranscriber} disabled={isDisabled} size="small" />
					</Tooltip>
					<TranscriptStatusTag status={transcriptStatus} />
				</Space>

				<Space>
					<Text type="secondary" style={{ fontSize: 11 }}>
						{transcriberActive ? "Listening..." : "Silent"}
					</Text>
					<AudioWave isActive={transcriberActive} analyzerNode={audioStreamingServiceRef.current?.analyser ?? undefined} visualizationStyle="bars" color="#52c41a" width={120} height={32} barCount={100} />
				</Space>
			</Space>

			{transcriberStatus && (
				<Alert
					data-testid="consultation-transcriber-status"
					type={transcriberStatus.state === "error" ? "error" : "warning"}
					message={transcriberStatus.message}
					showIcon
					banner
					style={{ marginBottom: 8, borderRadius: 8 }}
				/>
			)}

			{!transcripts.length ? (
				<Empty
					image={Empty.PRESENTED_IMAGE_SIMPLE}
					description={
						<Space direction="vertical">
							<Text>No transcripts yet. The system will generate transcripts as the consultation progresses.</Text>
							{!transcriberActive && (
								<Button type="primary" icon={<BulbOutlined />} onClick={toggleTranscriber}>
									Enable Transcription
								</Button>
							)}
						</Space>
					}
				/>
			) : (
				<div style={{ flex: 1, minHeight: 0, position: "relative" }}>
					<div ref={scrollRef} onScroll={handleScroll} data-testid="consultation-transcript-list" style={{ height: "100%", overflowY: "auto", maxHeight: "80vh" }}>
						<TransitionGroup component={null}>
							{transcripts.map((t) => (
								<CSSTransition key={t.id} timeout={600} classNames="slide-down">
									<Card
										size="small"
										style={{
											marginBottom: 8,
											borderRadius: 8,
										}}
									>
										{renderTranscriptContent(t)}
									</Card>
								</CSSTransition>
							))}
						</TransitionGroup>
					</div>

					{!pinnedToLatest && (
						<Button
							data-testid="consultation-transcript-jump-latest"
							size="small"
							type="primary"
							icon={<ArrowDownOutlined />}
							onClick={jumpToLatest}
							style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)" }}
						>
							Jump to latest
						</Button>
					)}
				</div>
			)}
		</div>
	);
};

export default TranscriptList;
